import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
  UseQueryOptions,
} from 'react-query';
import { ProjectId, ProjectConversationId, User } from 'api/models';
import {
  ProjectConversationsAPI,
  ProjectConversation,
  BaseProjectConversation,
  ProjectConversationPayloadSkelton,
  ProjectConversationLastSeenInfo,
} from 'api/project-conversations';
import { tenMinutes } from './constants';

interface MembersPayload {
  users: Partial<User>[];
  conversationId: ProjectConversationId;
}

interface LastSeenMutationPayload {
  payload: Partial<ProjectConversationLastSeenInfo>;
  conversationId: ProjectConversationId;
}

export const getProjectWideConversationsQueryKey = (
  projectId: ProjectId,
): (string | number)[] => ['project_conversation', ...projectId];

export const getProjectWideConversationIdQueryKey = (
  projectId: ProjectId,
  conversationId: ProjectConversationId,
): (string | number)[] => {
  return [...getProjectWideConversationsQueryKey(projectId), ...conversationId];
};

export const useProjectConversationsQuery = (
  projectId: ProjectId,
  options?: UseQueryOptions<
    ProjectConversation[],
    unknown,
    ProjectConversation[],
    (string | number)[]
  >,
): UseQueryResult<ProjectConversation[], unknown> => {
  return useQuery(
    getProjectWideConversationsQueryKey(projectId),
    () => ProjectConversationsAPI.getProjectConversationsList(projectId),
    {
      staleTime: tenMinutes,
      cacheTime: 0,
      ...(options || {}),
    },
  );
};

export const useGetProjectConversationsQuery = (
  projectId: ProjectId,
  conversationId: ProjectConversationId,
  options?: UseQueryOptions<
    ProjectConversation,
    unknown,
    ProjectConversation,
    (string | number)[]
  >,
): UseQueryResult<ProjectConversation, unknown> => {
  return useQuery(
    getProjectWideConversationIdQueryKey(projectId, conversationId),
    () => {
      return ProjectConversationsAPI.getProjectConversation(
        projectId,
        conversationId,
      );
    },
    {
      staleTime: tenMinutes,
      cacheTime: 0,
      ...(options || {}),
    },
  );
};

export const useCreateProjectConversationsMutation = (
  projectId: ProjectId,
): UseMutationResult<ProjectConversation, unknown, BaseProjectConversation> => {
  const queryClient = useQueryClient();

  return useMutation<ProjectConversation, unknown, BaseProjectConversation>(
    (payload: ProjectConversationPayloadSkelton) =>
      ProjectConversationsAPI.createProjectConversation(projectId, payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectWideConversationsQueryKey(projectId),
        );
      },
    },
  );
};

export const useUpdateProjectConversationsMutation = (
  projectId: ProjectId,
  conversationId: ProjectConversationId,
): UseMutationResult<
  ProjectConversation,
  unknown,
  Partial<BaseProjectConversation>
> => {
  const queryClient = useQueryClient();

  return useMutation<
    ProjectConversation,
    unknown,
    Partial<BaseProjectConversation>
  >(
    (payload: Partial<BaseProjectConversation>) =>
      ProjectConversationsAPI.updateProjectConversation(
        projectId,
        conversationId,
        payload,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectWideConversationsQueryKey(projectId),
        );
      },
    },
  );
};

export const useDeleteProjectConversationsMutation = (
  projectId: ProjectId,
): UseMutationResult<ProjectConversation, unknown, ProjectConversationId> => {
  const queryClient = useQueryClient();

  return useMutation<ProjectConversation, unknown, ProjectConversationId>(
    (conversationId: ProjectConversationId) =>
      ProjectConversationsAPI.deleteProjectConversation(
        projectId,
        conversationId,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectWideConversationsQueryKey(projectId),
        );
      },
    },
  );
};

export const useAddMembersProjectConversationsMutation = (
  projectId: ProjectId,
): UseMutationResult<ProjectConversation, unknown, MembersPayload> => {
  const queryClient = useQueryClient();

  return useMutation<ProjectConversation, unknown, MembersPayload>(
    ({ users, conversationId }) =>
      ProjectConversationsAPI.addMembersToConversation(
        projectId,
        conversationId,
        users,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectWideConversationsQueryKey(projectId),
        );
      },
    },
  );
};

export const useUpdateMembersLastSeenMutation = (
  projectId: ProjectId,
): UseMutationResult<ProjectConversation, unknown, LastSeenMutationPayload> => {
  return useMutation<ProjectConversation, unknown, LastSeenMutationPayload>(
    ({ payload, conversationId }) =>
      ProjectConversationsAPI.updateMembersLastSeen(
        projectId,
        conversationId,
        payload,
      ),
  );
};

export const useRemoveMembersProjectConversationsMutation = (
  projectId: ProjectId,
): UseMutationResult<ProjectConversation, unknown, MembersPayload> => {
  const queryClient = useQueryClient();

  return useMutation<ProjectConversation, unknown, MembersPayload>(
    ({ users, conversationId }) =>
      ProjectConversationsAPI.removeMembersToConversation(
        projectId,
        conversationId,
        users,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectWideConversationsQueryKey(projectId),
        );
      },
    },
  );
};
